import { zodResolver } from '@hookform/resolvers/zod';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller,useForm } from 'react-hook-form';

import { markBackfillAccessoriesRequestedFormSchema } from '@ekhodealer/ekho-common';
import { Button, Checkbox, Group, SkeletonRect,Stack, Text, toast } from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { formatTimestampToDate } from '@common/lib/time';

import { useGetMarkBackfillAccessoriesRequested } from '../../../api/progress/useGetMarkBackfillAccessoriesRequested';
import { useMarkBackfillAccessoriesRequested } from '../../../api/progress/useMarkBackfillAccessoriesRequested';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface MarkBackfillAccessoriesRequestedDetailProps {
	actionItem: ActionItem;
}

interface FormData {
	isConfirmed: boolean;
}

/**
 * MarkBackfillAccessoriesRequestedDetail component for marking backfill accessories as requested.
 * Displays a checkbox for confirmation and a submit button.
 */
export const MarkBackfillAccessoriesRequestedDetail = ({
	actionItem,
}: MarkBackfillAccessoriesRequestedDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const markBackfillAccessoriesRequestedMutation = useMarkBackfillAccessoriesRequested(orderId);
	const { data: existingData, isLoading: isLoadingData } =
		useGetMarkBackfillAccessoriesRequested(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<FormData>({
		resolver: zodResolver(markBackfillAccessoriesRequestedFormSchema),
		defaultValues: {
			isConfirmed: false,
		},
	});

	// Pre-populate form with existing data
	useEffect(() => {
		if (existingData?.dateAccessoriesRequested) {
			reset({ isConfirmed: true });
		}
	}, [existingData, reset]);

	const submitHandler = handleSubmit(async (data: FormData) => {
		if (!data.isConfirmed) {
			return;
		}

		try {
			await markBackfillAccessoriesRequestedMutation.mutateAsync();
			toast({
				message: 'Backfill accessories marked as requested successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form on success
			reset();
		} catch (err) {
			const errorMessage =
				err instanceof Error
					? err.message
					: 'Failed to mark backfill accessories as requested';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Confirm that backfill accessories have been requested. The date will be recorded automatically when you submit.'}
				</Text>
			</Stack>

			<Stack itemsSpacing='16'>
				{/* Show completion status if already complete */}
				{isLoadingData ? (
					<SkeletonRect width='100%' height={48} />
				) : existingData?.dateAccessoriesRequested ? (
					<Stack itemsSpacing='4'>
						<Text size='body' color='success'>
							✓ Accessories requested
						</Text>
						<Text size='bodySmall' color='secondary'>
							Requested on{' '}
							{formatTimestampToDate(new Date(existingData.dateAccessoriesRequested).getTime()) ?? '—'}
						</Text>
					</Stack>
				) : null}

				{/* Show form if not complete */}
				{!existingData?.dateAccessoriesRequested && (
					<>
						<Controller
							name='isConfirmed'
							control={control}
							render={({ field: { onChange, value } }) => (
								<Stack itemsSpacing='4'>
									<Group itemsSpacing='8' itemsAlignY='center' itemsAlignX='start'>
										<Checkbox
											isSelected={value}
											onChange={onChange}
											isDisabled={
												isSubmitting ||
												markBackfillAccessoriesRequestedMutation.isPending
											}
											ariaLabel='Confirm backfill accessories have been requested'
										/>
										<Text size='bodySmall'>Confirm</Text>
									</Group>
									{errors.isConfirmed && (
										<Text size='footnote' color='error'>
											{errors.isConfirmed.message}
										</Text>
									)}
								</Stack>
							)}
						/>

						<Button
							size='large'
							isFullWidth
							onPress={() => {
								submitHandler();
							}}
							isLoading={isSubmitting || markBackfillAccessoriesRequestedMutation.isPending}
							isDisabled={isLoadingData}>
							{actionItem.config.buttonTitle || 'Mark accessories as requested'}
						</Button>
					</>
				)}
			</Stack>
		</Stack>
	);
};
